/**
 * AI 生成排版的前端接口层。
 *
 * key 的存放策略：用户自己在 apibobo.com 充值、费用从他账号扣，所以这把 key
 * 是**他的密钥**。只写 localStorage，**刻意不进模板云同步的 KV** —— 模板同步是
 * 为了跨设备复用排版，把密钥捎带上云是另一回事，风险和收益不对等。代价是换设备
 * 要重填一次，可以接受。
 */
import type { ContractTemplate } from '../contract/types';
import type { FieldMeta } from '../api/types';

const API_BASE = 'https://printpro.xiaomiao.win';
const KEY_STORAGE = 'pp_ai_apikey';

export function loadApiKey(): string {
  try {
    return localStorage.getItem(KEY_STORAGE) || '';
  } catch {
    return '';
  }
}

export function saveApiKey(key: string): void {
  try {
    const v = key.trim();
    if (v) localStorage.setItem(KEY_STORAGE, v);
    else localStorage.removeItem(KEY_STORAGE);
  } catch {
    /* 隐私模式下写不进就算了，本次会话仍可用 */
  }
}

export interface TemplateIssue {
  kind: 'hallucinated_field' | 'repaired_table';
  detail: string;
}

export interface GenerateResult {
  /** 这次是否用掉了一次免费额度。 */
  usedTrial?: boolean;
  trialRemaining?: number | null;
  template: ContractTemplate;
  issues: TemplateIssue[];
  model: string;
  usage: Record<string, number> | null;
}

async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  return body?.message || `请求失败（HTTP ${res.status}）`;
}

/** 前端能接受的样本类型 —— 与实测通过的三类对齐。 */
export const SAMPLE_ACCEPT = '.pdf,.docx,.doc,image/*';

/**
 * 把样本文件读成 data URL 内联进请求。
 *
 * 不先传到我们的服务器再给地址：实测 `file.file_data` 这条传法对 PDF / Word /
 * 图片三种格式都通，内联既少一次往返，用户的单据也**不会落进我们的存储**。
 */
export function readSampleAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('读取文件失败'));
    r.readAsDataURL(file);
  });
}

export interface AiTrialQuota {
  trialLimit: number;
  trialUsed: number;
  trialRemaining: number;
}

/** 还能免费生成几次。取不到就当没有免费额度（界面退回「请填 Key」）。 */
export async function fetchAiQuota(accountId: string): Promise<AiTrialQuota> {
  try {
    const res = await fetch(`${API_BASE}/api/ai/quota?accountId=${encodeURIComponent(accountId)}`);
    if (!res.ok) throw new Error('quota');
    return (await res.json()) as AiTrialQuota;
  } catch {
    return { trialLimit: 0, trialUsed: 0, trialRemaining: 0 };
  }
}

export async function generateTemplate(input: {
  /** 留空则走免费试用额度（费用我们出）。 */
  apiKey: string;
  accountId: string;
  tableName: string;
  fieldMetas: FieldMeta[];
  sample?: { fileName: string; dataUrl: string };
}): Promise<GenerateResult> {
  const res = await fetch(`${API_BASE}/api/ai/template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      apiKey: input.apiKey,
      accountId: input.accountId,
      tableName: input.tableName,
      sample: input.sample || null,
      fields: input.fieldMetas.map((f) => ({ name: f.name, type: f.type })),
    }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as GenerateResult;
}
